import React from 'react'
import { Typography, type TypographyProps, Skeleton, type SkeletonProps, styled } from '@mui/material'
import { css, type SerializedStyles } from '@emotion/react'

type Animation = 'pulse' | 'wave' | false
interface Props extends TypographyProps {
    loading?: boolean
    loaderWidth: string | number
    dataTest?: string
    height?: string | number
    animation?: Animation
    skeletonVariant?: SkeletonProps['variant']
}

const makeAlign = (align?: 'left' | 'center' | 'right' | 'justify' | 'inherit'): SerializedStyles => {
    switch (align) {
        case 'left':
            return css`
                justify-content: flex-start;
            `
        case 'center':
            return css`
                justify-content: center;
            `
        case 'right':
            return css`
                justify-content: flex-end;
            `
        case 'justify':
            return css`
                flex-grow: 1;
            `
        default:
            return css``
    }
}

interface AlignProps {
    justifyContent: SerializedStyles
}

const AlignDiv = styled('div')<AlignProps>`
    display: inherit;
    width: 100%;
    display: flex;
    ${({ justifyContent }: AlignProps) => justifyContent}
`

export const TypographyLoader: React.VFC<Props> = ({
    loading,
    height,
    skeletonVariant,
    animation,
    dataTest,
    loaderWidth,
    align = 'inherit',
    children,
    ...rest
}: Props) => {
    let alignVal = align
    const ref = React.useRef<HTMLSpanElement | null>(null)
    if (ref.current && align === 'inherit') {
        const { textAlign } = window.getComputedStyle(ref.current)
        switch (textAlign) {
            case 'left':
                alignVal = 'left'
                break
            case 'center':
                alignVal = 'center'
                break
            case 'right':
                alignVal = 'right'
                break
            case 'justify':
                alignVal = 'left'
                break
            default:
                throw new Error('Unhandled text align')
        }
    }

    const justify = makeAlign(alignVal)

    return (
        <Typography {...rest} ref={ref}>
            {loading
                ? ref.current && (
                      <AlignDiv justifyContent={justify}>
                          <Skeleton
                              variant={skeletonVariant}
                              height={height}
                              animation={animation}
                              width={loaderWidth}
                          />
                      </AlignDiv>
                  )
                : children}
        </Typography>
    )
}
